import { useEffect, useMemo, useState } from "react";
import type { StatusColor } from "../../../../components/Status/Status";
import { StatusLeft } from "../../../../components/Status/Status";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import {
  defaultHandleSort,
  DEFAULT_PER_PAGE,
  formatDateTime,
  toTitleCase,
  useStoreState,
} from "../../../../util/util";
import type { ColumnDef, Getter, Row } from "@tanstack/react-table";
import { IDMobile } from "../../../../components/IDs/IDs";
import useSWR from "swr";
import type { WithPagination } from "../../../../types/types";
import { InvisibleButton } from "../../../../components/Buttons/Buttons";
import { DownloadIcon } from "../../../../components/Icons/Icons";
import type { SortingRule } from "react-table";
import { H3 } from "../../../../components/Typography/Typography";
import { DropdownContainer } from "../../../../components/Layout/Layout";
import { DropDown } from "../../../../components/DropDown/DropDown";
import { ContentWrapper } from "../../../../layout/portalPageLayout";
import { Table } from "../../../../components/Table/Table";
import { Pagination } from "../../../../components/Pagination/Pagination";
import { createChangePerPageHandler } from "../../../../components/Pagination/util";
import { BooleanParam, useQueryParams } from "use-query-params";
import type { PriceTierUploadSchema } from "./types";

const getStatusColor = (
  status: PriceTierUploadSchema["derived_status"]
): StatusColor => {
  switch (status) {
    case "has_errors":
      return "red";
    case "completed":
      return "green";
    case "pending":
    case "processing":
      return "yellow";
    default:
      return "orange";
  }
};

const getStatusText = (
  status: PriceTierUploadSchema["derived_status"],
  t: TFunction
): string => {
  switch (status) {
    case "has_errors":
      return t("Failed");
    case "completed":
      return t("Successful");
    case "pending":
    case "processing":
      return t("In Progress");
    default:
      return t("Paused");
  }
};

export const PreviousPriceTiersUploadTable = () => {
  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "desc",
  });
  const [tableData, setTableData] = useState<
    (PriceTierUploadSchema & { id: string })[]
  >([]);
  const [offset, setOffset] = useState(0);
  const perPageItems = [10, 20, 50];
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });
  const [query, setQuery] = useQueryParams({
    shouldMutate: BooleanParam,
  });

  const { storefront_id } = useStoreState();
  const { t } = useTranslation();

  const get_url = ({
    status,
    download_url,
    error_log_url,
  }: {
    status: PriceTierUploadSchema["derived_status"];
    download_url?: string;
    error_log_url?: string;
  }) =>
    status === "has_errors" && !!error_log_url
      ? error_log_url
      : !!download_url
      ? download_url
      : null;

  const tableColumns = useMemo<ColumnDef<PriceTierUploadSchema>[]>(
    () => [
      {
        header: t("Upload ID"),
        accessorKey: "number",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <IDMobile>{getValue()}</IDMobile>
        ),
      },
      {
        header: t("Uploaded on"),
        accessorKey: "uploaded_at",
        enableSorting: false,
        cell: (cell) => cell.renderValue(),
      },
      {
        header: t("Uploaded By"),
        accessorKey: "created_by",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <span>{toTitleCase(getValue())}</span>
        ),
      },
      {
        header: t("Status"),
        accessorKey: "derived_status",
        enableSorting: false,
        cell: ({
          getValue,
        }: {
          getValue: Getter<PriceTierUploadSchema["derived_status"]>;
        }) => (
          <StatusLeft
            color={getStatusColor(getValue())}
            text={getStatusText(getValue(), t)}
            textStyle={{ fontSize: "13px" }}
          />
        ),
      },
      {
        header: "",
        accessorKey: "download_url",
        enableSorting: false,
        cell: ({
          row: {
            original: { download_url, error_log_url, derived_status },
          },
        }: {
          row: Row<PriceTierUploadSchema>;
        }) =>
          get_url({ status: derived_status, download_url, error_log_url }) ? (
            <a
              href={
                get_url({
                  status: derived_status,
                  download_url,
                  error_log_url,
                })!
              }
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", width: "fit-content" }}
            >
              <InvisibleButton title={t("Log report")}>
                <DownloadIcon width={16} height={16} />
              </InvisibleButton>
            </a>
          ) : (
            <></>
          ),
      },
    ],
    [t]
  );

  const {
    data: previousUploadsResponse,
    error,
    mutate,
  } = useSWR<WithPagination<{ data: PriceTierUploadSchema[] }>>(
    `/v2/storefronts/${storefront_id}/price-tiers/bulk-upload-jobs?offset=${offset}&limit=${perPage}&order_by=${
      sortingRules.orderBy ?? "desc"
    }`
  );

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const isLoading = !previousUploadsResponse && !error;

  useEffect(() => {
    const handlePreviousUpdatesData = ({
      data,
      pagination,
    }: WithPagination<{ data: PriceTierUploadSchema[] }>) => {
      setTableData(
        data.map(({ upload_id, uploaded_at, ...rest }) => ({
          ...rest,
          upload_id,
          id: upload_id,
          uploaded_at: formatDateTime(uploaded_at),
        }))
      );
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    };
    if (previousUploadsResponse) {
      handlePreviousUpdatesData(previousUploadsResponse);
    }
  }, [perPage, previousUploadsResponse]);

  useEffect(() => {
    if (query.shouldMutate) {
      mutate();
      setQuery({ shouldMutate: false });
    }
  }, [query.shouldMutate, mutate, setQuery]);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <H3 style={{ margin: 0 }}>{t("Previous Uploads")}</H3>
      <div>
        <DropdownContainer>
          <DropDown
            items={perPageItems}
            activeItem={perPage}
            textLeft={t("items") + ":"}
            textRight={t(" Per Page")}
            direction={"right"}
            className={"per_Page"}
            clickHandler={changePerPage}
          />
        </DropdownContainer>
        <ContentWrapper>
          <Table
            columns={tableColumns}
            data={tableData}
            isLoading={isLoading}
            error={error}
            handleSort={handleSort}
            lastChildleftAlign={true}
          />
          <Pagination
            pagination={tablePagination}
            offset={offset}
            handlePageClick={changePage}
          />
        </ContentWrapper>
      </div>
    </div>
  );
};
